import type {
  SpendingGoalWithProgress,
  WorkspaceMemberWithUser,
  LogCategory,
  LogMethod,
} from "@repo/share-log-interfaces";
import { Pencil, Trash2 } from "lucide-react";
import { cn } from "@repo/share-log-web-utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@web/components/ui/card";
import { Badge } from "@web/components/ui/badge";
import { Switch } from "@web/components/ui/switch";
import { Button } from "@web/components/ui/button";

interface SpendingGoalCardProps {
  goal: SpendingGoalWithProgress;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (isActive: boolean) => void;
  canManage: boolean;
  members: WorkspaceMemberWithUser[];
  categories: LogCategory[];
  methods: LogMethod[];
}

const OVER_BUDGET_PERCENT = 100;
const WARNING_BUDGET_PERCENT = 80;

function getProgressColor(percent: number) {
  if (percent >= OVER_BUDGET_PERCENT) return "bg-expense";
  if (percent >= WARNING_BUDGET_PERCENT) return "bg-warning";
  return "bg-success";
}

function getProgressBgColor(percent: number) {
  if (percent >= OVER_BUDGET_PERCENT) return "bg-expense-muted";
  if (percent >= WARNING_BUDGET_PERCENT) return "bg-warning-muted";
  return "bg-success-muted";
}

export function SpendingGoalCard({
  goal,
  onEdit,
  onDelete,
  onToggleActive,
  canManage,
  members,
  categories,
  methods,
}: SpendingGoalCardProps) {
  const categoryNames = goal.categoryIds.length > 0
    ? goal.categoryIds
        .map((id) => categories.find((c) => c.id === id)?.name)
        .filter(Boolean)
    : [];

  const methodNames = goal.methodIds.length > 0
    ? goal.methodIds
        .map((id) => methods.find((m) => m.id === id)?.name)
        .filter(Boolean)
    : [];

  const userNames = goal.userIds.length > 0
    ? goal.userIds
        .map((id) => members.find((m) => m.userId === id)?.user.nickname)
        .filter(Boolean)
    : [];

  const months = [goal.currentMonth, goal.previousMonth];

  return (
    <Card className={cn(!goal.isActive && "opacity-60")}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{goal.name}</CardTitle>
          {canManage && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {goal.isActive ? "활성" : "비활성"}
              </span>
              <Switch
                checked={goal.isActive}
                onCheckedChange={onToggleActive}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bars */}
        <div className="space-y-3">
          {months.map((m, i) => {
            const clamped = Math.min(m.percent, 100);
            return (
              <div key={i === 0 ? "current" : "previous"} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-8 shrink-0">
                    {m.month}월
                  </span>
                  <div
                    className={cn(
                      "relative h-2 w-full overflow-hidden rounded-full",
                      getProgressBgColor(m.percent),
                    )}
                  >
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        getProgressColor(m.percent),
                      )}
                      style={{ width: `${clamped}%` }}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground pl-10">
                  {m.amount.toLocaleString("ko-KR")}원 /{" "}
                  {goal.targetAmount.toLocaleString("ko-KR")}원{" "}
                  <span
                    className={cn(
                      "font-medium",
                      m.percent >= OVER_BUDGET_PERCENT
                        ? "text-expense"
                        : m.percent >= WARNING_BUDGET_PERCENT
                          ? "text-warning-foreground"
                          : "text-success-foreground",
                    )}
                  >
                    ({m.percent}%)
                  </span>
                </p>
              </div>
            );
          })}
        </div>

        {/* Filter badges */}
        <div className="flex flex-wrap gap-1.5">
          {categoryNames.length > 0 ? (
            categoryNames.map((name) => (
              <Badge key={name} variant="default" shape="pill">
                {name}
              </Badge>
            ))
          ) : (
            <Badge variant="muted" shape="pill">
              카테고리: 전체
            </Badge>
          )}
          {methodNames.length > 0 ? (
            methodNames.map((name) => (
              <Badge key={name} variant="info" shape="pill">
                {name}
              </Badge>
            ))
          ) : (
            <Badge variant="muted" shape="pill">
              수단: 전체
            </Badge>
          )}
          {userNames.length > 0 ? (
            userNames.map((name) => (
              <Badge key={name} variant="success" shape="pill">
                {name}
              </Badge>
            ))
          ) : (
            <Badge variant="muted" shape="pill">
              사용자: 전체
            </Badge>
          )}
        </div>

        {/* Footer actions */}
        {canManage && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-1" />
              수정
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 mr-1" />
              삭제
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
